// Die Standardliste fürs Material (E3, S5; Gernots Entscheidung 1: Günther
// tippt die echte Liste selbst ein) – was die Seite rechnet, ohne React.
//
// Jede Zeile hat zwei Stände: was am Server liegt (`gespeichert`, null bei
// einem neuen Eintrag, der noch nie gespeichert wurde) und was gerade in den
// Feldern steht (`entwurf`, als Text, wie getippt). Gespeichert wird beim
// Verlassen eines Feldes – nur, wenn der Entwurf gültig ist (dieselben
// Regeln wie am Server: Kern, `standardmaterialEingabe`) und sich wirklich
// etwas geändert hat. Einträge werden nie gelöscht, nur „aus der Liste
// genommen“ (inaktiv) – die Freigaben von gestern verweisen nicht auf sie,
// sie tragen Kopien (buero-vertrag.ts).
//
// Seit E3b (O65): die Gruppe für Varianten („Kabelkanal“) – Einträge mit
// derselben Gruppe sind in der Doku-App eine Kachel mit ihren Größen.

import { aufZweiStellen, standardmaterialEingabe, standardVergleich, type MaterialArt, type StandardmaterialEingabe, type StandardmaterialZeile } from "@happy-energy/kern";

/** Was in den Feldern einer Zeile steht – als Text, wie getippt. */
export interface Entwurf {
  bezeichnung: string;
  menge: string;
  einheit: string;
  artikelnummer: string;
  /** Seit E3b: die Gruppe für Varianten – leer: keine. */
  gruppe: string;
}

export interface ListenZeile {
  id: string;
  entwurf: Entwurf;
  art: MaterialArt;
  aktiv: boolean;
  /** Wie die Zeile am Server liegt – null: noch nie gespeichert (eben mit „+ Eintrag“ angelegt). */
  gespeichert: StandardmaterialZeile | null;
}

/** Eine Menge, wie Menschen sie tippen: „2“, „1,5“, „1.5“ – leer oder Unsinn: null. */
export function mengeLesen(text: string): number | null {
  const t = text.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Eine Menge zum Anzeigen im Feld: 1.5 → „1,5“. */
export function mengeText(menge: number): string {
  return String(menge).replace(".", ",");
}

/** Eine Zeile, wie sie vom Server kommt. */
export function zeileVomServer(z: StandardmaterialZeile): ListenZeile {
  return {
    id: z.id,
    entwurf: { bezeichnung: z.bezeichnung, menge: mengeText(z.menge), einheit: z.einheit, artikelnummer: z.artikelnummer ?? "", gruppe: z.gruppe ?? "" },
    art: z.art,
    aktiv: z.aktiv,
    gespeichert: z,
  };
}

/** Ein neuer, leerer Eintrag – die Kennung entsteht im Browser (Kern, `neueKennung`). */
export function neueZeile(id: string, art: MaterialArt): ListenZeile {
  return { id, entwurf: { bezeichnung: "", menge: "1", einheit: "Stk", artikelnummer: "", gruppe: "" }, art, aktiv: true, gespeichert: null };
}

/** Was an den Server geht – oder warum noch nicht (die Meldung steht an der Zeile). */
export type Pruefung = { ok: true; eingabe: StandardmaterialEingabe } | { ok: false; meldung: string };

export function eingabeAus(z: ListenZeile): Pruefung {
  const menge = mengeLesen(z.entwurf.menge);
  if (menge === null) return { ok: false, meldung: "Bitte eine Menge als Zahl eingeben (z. B. 2 oder 1,5)." };
  const gelesen = standardmaterialEingabe.safeParse({
    id: z.id,
    bezeichnung: z.entwurf.bezeichnung,
    menge,
    einheit: z.entwurf.einheit,
    artikelnummer: z.entwurf.artikelnummer,
    art: z.art,
    aktiv: z.aktiv,
    gruppe: z.entwurf.gruppe,
  });
  if (!gelesen.success) return { ok: false, meldung: gelesen.error.issues[0]?.message ?? "Bitte die Felder prüfen." };
  return { ok: true, eingabe: gelesen.data };
}

/** Muss das gespeichert werden? Neu immer; sonst nur, wenn sich am Server etwas ändern würde (Menge auf zwei Stellen, wie die Datenbank sie hält). */
export function mussSpeichern(z: ListenZeile, e: StandardmaterialEingabe): boolean {
  const s = z.gespeichert;
  if (s === null) return true;
  return (
    s.bezeichnung !== e.bezeichnung ||
    s.einheit !== e.einheit ||
    (s.artikelnummer ?? null) !== (e.artikelnummer ?? null) ||
    aufZweiStellen(s.menge) !== aufZweiStellen(e.menge) ||
    s.art !== e.art ||
    s.aktiv !== e.aktiv ||
    (s.gruppe ?? null) !== (e.gruppe ?? null)
  );
}

/** Wie die Zeile nach dem Speichern am Server liegt – aus der Eingabe, die angenommen wurde. */
export function alsGespeichert(e: StandardmaterialEingabe): StandardmaterialZeile {
  return {
    id: e.id,
    bezeichnung: e.bezeichnung,
    menge: aufZweiStellen(e.menge),
    einheit: e.einheit,
    artikelnummer: e.artikelnummer ?? null,
    art: e.art,
    aktiv: e.aktiv,
    gruppe: e.gruppe ?? null,
  };
}

/** Die drei Abschnitte der Seite – in der Reihenfolge der Liste: immer dabei, zur Auswahl, herausgenommen. */
export function abschnitte<Z extends Pick<ListenZeile, "art" | "aktiv">>(zeilen: readonly Z[]): { immer: Z[]; auswahl: Z[]; inaktiv: Z[] } {
  return {
    immer: zeilen.filter((z) => z.aktiv && z.art === "immer"),
    auswahl: zeilen.filter((z) => z.aktiv && z.art === "auswahl"),
    inaktiv: zeilen.filter((z) => !z.aktiv),
  };
}

/**
 * Die Zeilen in der Reihenfolge der Standardliste (seit dem Augentest E3b,
 * Gernot, 23.10.2026; Kern, standard-ordnung.ts): nach Gruppe, dann
 * Bezeichnung, Zahlen der Reihe nach – nach dem, was gespeichert ist, nicht
 * nach dem, was gerade getippt wird. Die Gruppe zählt nur bei „Zur Auswahl“
 * (nur dort ist sie zu sehen). Neue, noch nie gespeicherte Zeilen bleiben
 * unten, in der Reihenfolge, in der sie angelegt wurden.
 */
export function zeilenOrdnen<Z extends Pick<ListenZeile, "id" | "art" | "gespeichert">>(zeilen: readonly Z[]): Z[] {
  const schluessel = (z: Z) => ({ id: z.id, bezeichnung: z.gespeichert?.bezeichnung ?? "", gruppe: z.art === "auswahl" ? (z.gespeichert?.gruppe ?? null) : null });
  const gespeichert = zeilen.filter((z) => z.gespeichert !== null).sort((a, b) => standardVergleich(schluessel(a), schluessel(b)));
  return [...gespeichert, ...zeilen.filter((z) => z.gespeichert === null)];
}

/** Wie die zwei Abschnitte heißen – überall gleich: Titel, Satz oben, Knöpfe, „Herausgenommen“. */
export const ABSCHNITT: Readonly<Record<MaterialArt, string>> = { immer: "Bei jeder Anlage dabei", auswahl: "Zum Antippen draußen" };

/**
 * Seit E3c, S7 (G21; Gernot, 2.11.: „Immer dabei“ braucht mehr Aussagekraft,
 * bei „Zur Auswahl“ muss klar sein, dass es draußen als Kachel erscheint): je
 * Abschnitt ein Satz, was mit seinen Einträgen geschieht. Das Datenmodell
 * bleibt (`immer`/`auswahl`) – nur die Worte.
 */
export const ABSCHNITT_SATZ: Readonly<Record<MaterialArt, string>> = {
  immer: "Kommt von selbst in die Materialliste jeder Anlage. Die Menge lässt sich beim Freigeben ändern.",
  auswahl: "Erscheint in der HE-Doku als Kachel zum Antippen. Im Büro beim Freigeben mit „+ aus der Auswahl“.",
};

/**
 * Der Knopf, der eine Zeile in den anderen Abschnitt schiebt: Er sagt, wohin
 * – und im Hinweis, was das bei der Freigabe heißt. Seit dem Augentest am
 * 21.10.2026 (Gernot: „zur Auswahl“ / „immer dabei“ an jeder Zeile „passt
 * irgendwie nicht zusammen“).
 */
export function verschieben(art: MaterialArt): { nach: MaterialArt; knopf: string; erklaerung: string } {
  return art === "immer"
    ? { nach: "auswahl", knopf: "↓ zum Antippen", erklaerung: `Nach ‚${ABSCHNITT.auswahl}‘ – kommt nicht mehr von selbst in die Materialliste` }
    : { nach: "immer", knopf: "↑ bei jeder Anlage", erklaerung: `Nach ‚${ABSCHNITT.immer}‘ – kommt von selbst in die Materialliste jeder Anlage` };
}

/** Was eine Zeile gerade zeigt: wird gespeichert, ist gespeichert – oder warum nicht (fehlt: an der Eingabe; fehler: am Server). */
export type Status = { art: "speichert" } | { art: "gespeichert" } | { art: "fehlt"; meldung: string } | { art: "fehler"; meldung: string };

/**
 * Ein Zeichen für mehrere Zeilen – am zugeklappten Abschnitt „Herausgenommen“,
 * damit man auch ohne Aufklappen sieht, ob „aus der Liste nehmen“ gespeichert
 * ist: eine Meldung vor „speichert“ vor „gespeichert“; nichts, wenn keine
 * Zeile etwas zeigt.
 */
export function sammelStatus(alle: readonly (Status | undefined)[]): Status | undefined {
  return alle.find(hatMeldung) ?? alle.find((s) => s?.art === "speichert") ?? alle.find((s) => s?.art === "gespeichert");
}

/** Zeigt die Zeile eine Meldung? Im Abschnitt „Herausgenommen“ klappt er dann von selbst auf – sonst bliebe sie verborgen. */
export function hatMeldung(s: Status | undefined): s is Extract<Status, { meldung: string }> {
  return s?.art === "fehler" || s?.art === "fehlt";
}
